import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { Search, Filter, MapPin, ShoppingCart, Globe, ChevronDown } from 'lucide-react';
import { COUNTRIES_DATA } from './InteractiveGlobe';
import { useCurrency } from '../context/CurrencyContext';

// SVG paths for simplified world map regions
const MAP_REGIONS = {
  europe: {
    viewBox: "0 0 200 150",
    countries: [
      { code: 'PT', path: "M20,70 L25,65 L25,75 L20,80 Z", name: 'Portogallo' },
      { code: 'ES', path: "M25,65 L45,60 L50,75 L40,85 L25,80 Z", name: 'Spagna' },
      { code: 'FR', path: "M45,50 L70,45 L75,65 L50,70 L45,60 Z", name: 'Francia' },
      { code: 'GB', path: "M40,35 L50,30 L55,45 L45,50 Z", name: 'Regno Unito' },
      { code: 'DE', path: "M75,40 L95,35 L100,55 L80,60 L75,50 Z", name: 'Germania' },
      { code: 'IT', path: "M75,65 L85,60 L95,80 L80,90 L75,75 Z", name: 'Italia' },
      { code: 'PL', path: "M95,35 L115,30 L120,50 L100,55 Z", name: 'Polonia' },
      { code: 'AT', path: "M80,55 L95,52 L98,62 L82,65 Z", name: 'Austria' },
      { code: 'CH', path: "M70,55 L80,52 L82,60 L72,62 Z", name: 'Svizzera' },
      { code: 'NL', path: "M65,38 L75,35 L78,45 L68,48 Z", name: 'Paesi Bassi' },
      { code: 'GR', path: "M100,75 L115,70 L120,85 L105,90 Z", name: 'Grecia' },
      { code: 'TR', path: "M120,65 L150,60 L155,80 L125,85 Z", name: 'Turchia' },
    ]
  },
  asia: {
    viewBox: "0 0 250 180",
    countries: [
      { code: 'JP', path: "M200,50 L220,45 L225,70 L205,75 Z", name: 'Giappone' },
      { code: 'KR', path: "M185,55 L200,50 L205,70 L190,75 Z", name: 'Corea del Sud' },
      { code: 'CN', path: "M120,40 L180,30 L185,80 L125,90 Z", name: 'Cina' },
      { code: 'TH', path: "M140,100 L155,95 L160,120 L145,125 Z", name: 'Thailandia' },
      { code: 'SG', path: "M155,130 L165,128 L167,135 L157,137 Z", name: 'Singapore' },
      { code: 'IN', path: "M80,70 L120,60 L125,120 L85,130 Z", name: 'India' },
      { code: 'AE', path: "M55,90 L70,85 L75,100 L60,105 Z", name: 'Emirati Arabi' },
    ]
  },
  americas: {
    viewBox: "0 0 150 250",
    countries: [
      { code: 'CA', path: "M20,20 L100,15 L105,60 L25,65 Z", name: 'Canada' },
      { code: 'US', path: "M25,65 L105,60 L110,110 L30,115 Z", name: 'USA' },
      { code: 'MX', path: "M30,115 L70,110 L75,145 L35,150 Z", name: 'Messico' },
      { code: 'BR', path: "M50,160 L100,155 L105,220 L55,225 Z", name: 'Brasile' },
    ]
  },
  oceania: {
    viewBox: "0 0 200 150",
    countries: [
      { code: 'AU', path: "M40,50 L150,40 L160,120 L50,130 Z", name: 'Australia' },
      { code: 'NZ', path: "M165,100 L185,95 L190,130 L170,135 Z", name: 'Nuova Zelanda' },
    ]
  }
};

export function InteractiveMap({ products, onAddToCart, onCountrySelect }) {
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [hoveredCountry, setHoveredCountry] = useState(null);

  const regions = [
    { key: 'all', label: 'Tutti', icon: Globe },
    { key: 'europe', label: 'Europa', emoji: '' },
    { key: 'asia', label: 'Asia', emoji: '' },
    { key: 'americas', label: 'Americhe', emoji: '' },
    { key: 'africa', label: 'Africa', emoji: '' },
    { key: 'oceania', label: 'Oceania', emoji: '' },
  ];

  // Filter countries based on search and region
  const filteredCountries = useMemo(() => {
    return COUNTRIES_DATA.filter(country => {
      const matchesSearch = !searchQuery || 
        country.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        country.code.toLowerCase().includes(searchQuery.toLowerCase());
      
      const matchesRegion = selectedRegion === 'all' || country.region === selectedRegion;
      
      return matchesSearch && matchesRegion;
    });
  }, [searchQuery, selectedRegion]);

  // Get product for country (match by country_code)
  const getProductForCountry = (countryCode) => {
    return products?.find(p => p.country_code === countryCode);
  };


  return (
    <div className="min-h-screen bg-[#0f1419] pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl sm:text-4xl font-bold text-white font-outfit mb-4 flex items-center justify-center space-x-3">
              <MapPin className="w-8 h-8 text-teal-400" />
              <span>Mappa Destinazioni</span>
            </h1>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Clicca su un paese per vedere i piani eSIM disponibili. Usa i filtri per trovare rapidamente la tua destinazione.
            </p>
          </div>

          {/* Search and Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cerca paese..."
                className="w-full bg-white/5 border border-white/10 rounded-xl pl-12 pr-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-teal-500 transition-colors"
                data-testid="map-search"
              />
            </div>

            {/* Region filters */}
            <div className="flex flex-wrap gap-2">
              {regions.map(region => (
                <button
                  key={region.key}
                  onClick={() => setSelectedRegion(region.key)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                    selectedRegion === region.key
                      ? 'bg-teal-500 text-black'
                      : 'bg-white/5 text-slate-300 hover:bg-white/10'
                  }`}
                  data-testid={`map-filter-${region.key}`}
                >
                  {region.icon ? (
                    <region.icon className="w-4 h-4" />
                  ) : (
                    <span>{region.emoji}</span>
                  )}
                  <span>{region.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Countries Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {filteredCountries.map(country => {
              const product = getProductForCountry(country.code);
              const hasProduct = !!product;

              return (
                <motion.button
                  key={country.code}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  whileHover={{ scale: 1.02 }}
                  onClick={() => hasProduct && onCountrySelect(country)}
                  disabled={!hasProduct}
                  className={`relative p-4 rounded-xl border transition-all text-left ${
                    hasProduct
                      ? 'bg-white/5 border-white/10 hover:border-teal-500/50 hover:bg-white/10 cursor-pointer'
                      : 'bg-white/[0.02] border-white/5 opacity-60 cursor-not-allowed'
                  }`}
                  data-testid={`country-card-${country.code}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        hasProduct 
                          ? 'bg-gradient-to-br from-teal-500/20 to-indigo-500/20' 
                          : 'bg-slate-800/50'
                      }`}>
                        <MapPin className={`w-5 h-5 ${hasProduct ? 'text-teal-400' : 'text-slate-600'}`} />
                      </div>
                      <div>
                        <h3 className={`font-semibold ${hasProduct ? 'text-white' : 'text-slate-500'}`}>
                          {country.name}
                        </h3>
                        <p className="text-xs text-slate-500 uppercase">{country.code}</p>
                      </div>
                    </div>
                    
                    {hasProduct && (
                      <div className="text-right">
                        <p className="text-sm font-bold text-teal-400 font-mono">{t('products.from')} {formatPrice(product.price)}</p>
                      </div>
                    )}
                  </div>

                  {!hasProduct && (
                    <p className="mt-2 text-xs text-slate-600">Prossimamente</p>
                  )}
                </motion.button>
              );
            })}
          </div>

          {filteredCountries.length === 0 && (
            <div className="text-center py-16">
              <MapPin className="w-16 h-16 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400 text-lg">Nessun paese trovato</p>
              <p className="text-slate-500 text-sm mt-2">Prova con una ricerca diversa</p>
            </div>
          )}

          {/* Stats */}
          <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4">
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-teal-400 font-mono">{COUNTRIES_DATA.length}</p>
              <p className="text-sm text-slate-400">Paesi</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-teal-400 font-mono">
                {COUNTRIES_DATA.filter(c => c.productId).length}
              </p>
              <p className="text-sm text-slate-400">eSIM Disponibili</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-teal-400 font-mono">5</p>
              <p className="text-sm text-slate-400">Regioni</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-teal-400 font-mono">24/7</p>
              <p className="text-sm text-slate-400">Supporto</p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
